<template>
    <div ref="switchCountry" style="position: relative">
        <app-button secondary @click="menu = !menu">
            <template v-if="findSlot('selected')">
                <slot name="selected" v-bind="{ inputValue }" />
            </template>
            <template v-else>
                <div v-if="inputValue" class="d-flex align-center">
                    <v-img :src="inputValue.flag" height="1.2rem" width="1.2rem" contain />
                    <div v-if="!hideButtonText" class="font-weight-bold ml-2" style="font-size: 0.8rem">
                        {{ inputValue[`${itemText}`] }}
                    </div>
                    <v-icon right dense :class="menu ? 'fa-rotate-180' : false">
                        {{ getIcon('dropdown') }}
                    </v-icon>
                </div>
                <div v-else class="d-flex align-center">
                    <div v-if="!hideButtonText" class="font-weight-bold" style="font-size: 0.8rem">
                        {{ $t('select_a_country') }}
                    </div>
                    <v-icon right dense :class="menu ? 'fa-rotate-180' : false">
                        {{ getIcon('dropdown') }}
                    </v-icon>
                </div>
            </template>
        </app-button>

        <v-dialog v-model="menu" app width="auto" style="overflow: hidden">
            <app-card style="max-height: 65vh">
                <template #header>
                    <field-string
                        ref="searchInput"
                        v-model="search"
                        :label="$t('search')"
                        hide-details
                        class="mb-2"
                        :autocomplete="false"
                    >
                        <template v-if="search" #append>
                            <app-button icon="clear" icon-size="large" @click="search = null" />
                        </template>
                    </field-string>
                </template>
                <v-list-item-group v-if="results.length > 0" v-model="inputValue">
                    <v-list-item
                        v-for="(result, index) in results"
                        :key="index"
                        :value="result"
                        :inactive="inputValue && result[`${itemKey}`] !== inputValue[`${itemKey}`]"
                        :disabled="inputValue && result[`${itemKey}`] === inputValue[`${itemKey}`]"
                        color="primaryText"
                        dense
                        @click.stop="menu = false"
                    >
                        <template v-if="findSlot('search-result')">
                            <slot name="search-result" v-bind="{ result, inputValue }" />
                        </template>
                        <div v-else class="d-flex align-center">
                            <v-img :src="result.flag" height="1.2rem" width="1.2rem" contain />
                            <div class="ml-4" style="font-size: 0.9rem">
                                {{ result[`${itemText}`] }}
                            </div>
                        </div>
                    </v-list-item>
                </v-list-item-group>
                <div v-else>{{ $t('no_results_found') }}</div>
            </app-card>
        </v-dialog>
    </div>
</template>

<script>
import countries from '@/mixins/countries';
import i18nMixin from '@/mixins/i18n';
import wait from '@/lib/helpers/wait';

export default {
    name: 'SwitchCountry',
    mixins: [countries, i18nMixin],
    props: {
        value: {
            type: String,
            default: null
        },
        itemKey: {
            type: String,
            default: 'alpha2Code'
        },
        itemText: {
            type: String,
            default: 'name'
        },
        itemSearchKey: {
            type: String,
            default: 'name'
        },
        hideButtonText: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        returnObject: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        items: [],
        menu: false,
        search: null
    }),
    computed: {
        inputValue: {
            get() {
                return this.items.find(item => item[`${this.itemKey}`] === this.value) || null;
            },
            set(value) {
                if (this.required && !value) {
                    return;
                }

                if (!this.returnObject && typeof value === 'object' && this.itemKey in value) {
                    value = value[`${this.itemKey}`];
                }

                this.$emit('input', value);
            }
        },
        results() {
            const items = [...this.items];
            if (!this.search) {
                return items;
            }
            return items.filter(item => {
                return item[`${this.itemSearchKey}`].toLowerCase().includes(this.search);
            });
        }
    },
    watch: {
        async menu(v) {
            if (v) {
                await wait(100);
                this.$refs.searchInput.$el.querySelector('input').focus();
            }
        }
    },
    methods: {
        async setup() {
            if (!this.localeLoading) {
                await this.loadLocale();
            }

            this.items = this.countries.filter(country => {
                return this.itemKey in country && this.itemText in country;
            });

            // Set the default country based on the current users locale
            if (!this.value && this.itemKey in this.locale) {
                if (this.returnObject) {
                    this.inputValue = this.items.find(
                        item => item[`${this.itemKey}`] === this.locale[`${this.itemKey}`]
                    );
                } else {
                    this.inputValue = this.locale[`${this.itemKey}`];
                }
            }

            if (this.required && !this.inputValue) {
                if (this.returnObject) {
                    this.inputValue = this.items.find(item => item.name === 'United Kingdom');
                } else {
                    this.inputValue = this.items.find(item => item.name === 'United Kingdom')[`${this.itemKey}`];
                }
            }
        }
    }
};
</script>
